import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ConversationTurn,
  KnowledgeQueryFilters,
  KnowledgeQueryResult,
} from '@shared/knowledge';
import { queryKnowledge } from '../../services/api';
import { ChatMessage } from './ChatMessage';

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  chunks?: KnowledgeQueryResult[];
}

interface ChatPanelProps {
  filters?: KnowledgeQueryFilters;
}

const MAX_CONVERSATION_HISTORY = 10;

export function ChatPanel({ filters }: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [isQuerying, setIsQuerying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isQuerying, scrollToBottom]);

  const buildConversationHistory = (currentMessages: ChatTurn[]): ConversationTurn[] => {
    return currentMessages
      .slice(-MAX_CONVERSATION_HISTORY)
      .map((msg) => ({ role: msg.role, content: msg.content }));
  };

  const handleSubmit = async () => {
    const query = input.trim();
    if (!query || isQuerying) return;

    setError(null);
    setInput('');

    const userMessage: ChatTurn = { role: 'user', content: query };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setIsQuerying(true);

    try {
      const conversationHistory = buildConversationHistory(updatedMessages);
      const response = await queryKnowledge({
        query,
        limit: 5,
        filters,
        conversationHistory,
      });

      const assistantMessage: ChatTurn = {
        role: 'assistant',
        content: response.answer,
        chunks: response.chunks,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to query knowledge base.';
      setError(message);
    } finally {
      setIsQuerying(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSubmit();
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setError(null);
    inputRef.current?.focus();
  };

  return (
    <div className="flex h-full flex-col">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {messages.length === 0 && !isQuerying && (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-kaiva-coral/10">
                <svg
                  className="h-6 w-6 text-kaiva-coral"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z"
                  />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-foreground">
                Ask about handover knowledge
              </h3>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                Ask questions about captured interview knowledge. The system searches
                extracted insights and generates answers with source citations.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {[
                  'What are the key processes I need to know?',
                  'Who are the main stakeholders?',
                  'What unwritten rules should I be aware of?',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => {
                      setInput(suggestion);
                      inputRef.current?.focus();
                    }}
                    className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground transition hover:border-kaiva-coral/40 hover:text-foreground"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.map((msg, index) => (
          <ChatMessage
            key={`${msg.role}-${index}`}
            role={msg.role}
            content={msg.content}
            chunks={msg.chunks}
          />
        ))}

        {isQuerying && <ChatMessage role="assistant" content="" isLoading />}

        {error && (
          <div className="rounded-xl border border-kaiva-coral/30 bg-kaiva-coral/10 px-4 py-3 text-sm text-kaiva-coral">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-border bg-card px-4 py-3">
        {messages.length > 0 && (
          <div className="mb-2 flex justify-end">
            <button
              type="button"
              onClick={handleClearChat}
              className="text-xs text-muted-foreground transition hover:text-foreground"
              aria-label="Clear conversation"
            >
              Clear chat
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about handover knowledge..."
            rows={1}
            disabled={isQuerying}
            className="flex-1 resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground shadow-sm transition placeholder:text-muted-foreground focus:border-kaiva-teal focus:outline-none disabled:opacity-60"
            aria-label="Knowledge query input"
          />
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!input.trim() || isQuerying}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-kaiva-coral text-white shadow-sm transition hover:bg-kaiva-coral/90 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Send question"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
              />
            </svg>
          </button>
        </div>
        <p className="mt-2 text-[10px] text-muted-foreground">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
